'use client';

import { useCallback, useState } from 'react';

import { useRouter } from 'next/navigation';

import { useTranslation } from 'react-i18next';

import { toast } from '@kit/ui/sonner';

import {
  createPackagePurchaseCheckoutAction,
  purchasePackageWithWalletAction,
} from '../server/packages/package-purchase-actions';
import type { SelectedAccount } from '../types';
import type {
  HyperpayCheckoutData,
  MoyasarCheckoutData,
} from './use-booking-reducer';

export interface PlayerDetail {
  name: string;
  phone: string;
}

// Reuse the reducer's canonical checkout shapes rather than declaring parallel
// ones here: /book feeds these straight into SET_CHECKOUT_DATA, so a second
// definition would be free to drift from the type the dialogs actually consume.
export type {
  HyperpayCheckoutData,
  MoyasarCheckoutData,
} from './use-booking-reducer';

/**
 * How a surface reacts to each provider outcome. The hook itself never opens a
 * dialog: /book routes these into its booking reducer, while /packages holds
 * them in local component state. That keeps the provider branching — the part
 * that must never drift between surfaces — in exactly one place.
 */
export interface PackagePurchaseCallbacks {
  /** Close the package checkout dialog (every non-redirect branch does this). */
  onCloseCheckoutDialog: () => void;
  onHyperpay: (data: HyperpayCheckoutData) => void;
  onStripe: (clientSecret: string) => void;
  onMoyasar: (data: MoyasarCheckoutData) => void;
  /**
   * Wallet purchase succeeded. The surface decides where the customer lands:
   * /book navigates to /packages (the purchase is not visible where they are),
   * while /packages is already the destination and only refreshes. Keeping the
   * navigation here — rather than in the hook — is what lets the dialog stay
   * put on /packages instead of bouncing the customer to another tab.
   */
  onWalletSuccess: (newBalance: number) => void;
}

/**
 * Owns package-purchase checkout: the four provider branches (redirect,
 * HyperPay widget, embedded Stripe, Moyasar form) and the wallet path.
 *
 * Extracted from booking-form.tsx so /book and the native /packages catalog
 * share ONE implementation. The logic here is payment-adjacent and full of
 * hard-won rules (see the metadata comments below); two copies would drift and
 * the drift would surface as a checkout bug in production.
 *
 * The hook deliberately takes no booking state — package purchase never reads
 * the selected court or slot — so it is safe to mount on any surface.
 */
export function usePackagePurchase(callbacks: PackagePurchaseCallbacks) {
  const { t } = useTranslation('bookings');
  const router = useRouter();
  const [isProcessing, setIsProcessing] = useState(false);

  const {
    onCloseCheckoutDialog,
    onHyperpay,
    onStripe,
    onMoyasar,
    onWalletSuccess,
  } = callbacks;

  const purchase = useCallback(
    async (
      packageId: string,
      playerCount?: number,
      price?: number,
      selectedAccounts?: SelectedAccount[],
      discountCode?: string,
      playerDetails?: PlayerDetail[],
      // Customer-chosen validity start (YYYY-MM-DD). Only present when a
      // future date was picked in the dialog; the action re-validates it.
      startDate?: string,
      /** Where the PSP returns to. Defaults to /book/return, which reconciles
       *  every surface's package purchase via `type=package`. */
      returnUrl?: string,
    ) => {
      setIsProcessing(true);
      try {
        const purchaseForUserIds =
          selectedAccounts && selectedAccounts.length > 0
            ? selectedAccounts.map((acc) => acc.id)
            : undefined;

        const result = await createPackagePurchaseCheckoutAction({
          packageId,
          returnUrl: returnUrl ?? `${window.location.origin}/book/return`,
          playerCount,
          price,
          purchaseForUserIds,
          discountCode,
          playerDetails,
          startDate,
        });

        if (result.success && result.data) {
          const checkout = result.data.checkout as
            | {
                mode: string;
                redirectUrl?: string;
                clientSecret?: string;
                provider?: string;
                checkoutId?: string;
                integrity?: string;
              }
            | undefined;

          // Redirect-mode providers — redirect immediately.
          if (checkout?.mode === 'redirect' && checkout.redirectUrl) {
            window.location.href = checkout.redirectUrl;
            return;
          }

          // HyperPay embedded COPYandPAY widget — open the HyperPay dialog.
          // Mirrors the court flow's onHyperpayCheckout. The widget needs the
          // HyperPay checkoutId (not our purchaseId) as sessionId. returnPath
          // carries the package context so the frictionless (non-3DS) success
          // reconciles via the return page's package branch (type=package)
          // rather than the court-booking branch.
          if (
            checkout?.mode === 'embedded_widget' &&
            checkout.provider === 'hyperpay' &&
            checkout.checkoutId
          ) {
            const purchaseId = result.data.purchaseId ?? result.data.sessionId;
            onHyperpay({
              sessionId: checkout.checkoutId,
              integrity: checkout.integrity,
              provider: 'hyperpay',
              returnPath: `/book/return?type=package&package_id=${encodeURIComponent(
                packageId,
              )}&purchase_id=${encodeURIComponent(purchaseId)}`,
            });
            onCloseCheckoutDialog();
            return;
          }

          // Stripe embedded checkout — open the Stripe dialog.
          if (checkout?.mode === 'embedded_stripe' && checkout.clientSecret) {
            onStripe(checkout.clientSecret);
            onCloseCheckoutDialog();
            return;
          }

          // Embedded-form providers (Moyasar) — open the dialog. Forward the
          // discount snapshot via extraMetadata so the discount_code /
          // original_unit_price / discount_acting_user_id reach Moyasar's
          // metadata → the return-page/webhook redemption write (which persists
          // the user_booking_packages discount columns + discount_addon_id that
          // gates one-time-use). Without this the discount applies to the charge
          // but is never recorded and the code stays infinitely reusable.
          onMoyasar({
            ...result.data,
            extraMetadata: {
              ...(result.data.discountMeta ?? {}),
              // price_per_package is what the webhook/return-page read for
              // the charged amount; carry it explicitly too.
              price_per_package: String(
                result.data.packageData?.pricePerPackage ??
                  result.data.packageData?.price ??
                  '',
              ),
              // Per-player name/phone (player-priced packages, 2+ players).
              // JSON string; the return-page/webhook persist it to
              // user_booking_packages.player_details. extraMetadata is spread
              // last into the Moyasar payment metadata (see moyasar-checkout-dialog).
              ...(playerDetails && playerDetails.length > 0
                ? { player_details: JSON.stringify(playerDetails) }
                : {}),
              // Customer-chosen validity start. MUST be carried here: the
              // Moyasar card widget is a client-side FORM integration, so
              // createCheckout() is a no-op and the metadata the action built
              // server-side never reaches Moyasar — the dialog rebuilds it from
              // extraMetadata. Without this the picked start is silently
              // dropped on the default card path (Apple Pay stamps its own).
              // The action already validated it (book-by, no window, >= today).
              ...(startDate ? { start_date: startDate } : {}),
            },
          });
          onCloseCheckoutDialog();
        } else {
          toast.error(result.error || t('toastCheckoutSessionFailed'));
        }
      } catch {
        toast.error(t('toastPackagePurchaseFailed'));
      } finally {
        setIsProcessing(false);
      }
    },
    [onCloseCheckoutDialog, onHyperpay, onStripe, onMoyasar, t],
  );

  const purchaseWithWallet = useCallback(
    async (
      packageId: string,
      playerCount: number | undefined,
      price: number | undefined,
      selectedAccounts: SelectedAccount[],
      discountCode: string | undefined,
      playerDetails: PlayerDetail[] | undefined,
      startDate: string | undefined,
      purchaseReference: string,
    ) => {
      setIsProcessing(true);

      try {
        const result = await purchasePackageWithWalletAction({
          packageId,
          playerCount,
          price,
          purchaseForUserIds: selectedAccounts.map((account) => account.id),
          discountCode,
          playerDetails,
          startDate,
          purchaseReference,
        });

        if (!result.success) {
          toast.error(
            result.error ||
              t('toastPackageWalletFailed', {
                defaultValue:
                  'Could not pay for this package with your wallet.',
              }),
          );
          return false;
        }

        onWalletSuccess(result.data.new_balance);
        onCloseCheckoutDialog();
        toast.success(
          t('toastPackageWalletSuccess', {
            defaultValue: 'Package paid from your wallet!',
          }),
        );
        router.refresh();
        return true;
      } catch {
        toast.error(
          t('toastPackageWalletFailed', {
            defaultValue: 'Could not pay for this package with your wallet.',
          }),
        );
        return false;
      } finally {
        setIsProcessing(false);
      }
    },
    [onCloseCheckoutDialog, onWalletSuccess, router, t],
  );

  return { purchase, purchaseWithWallet, isProcessing };
}
